import { useState } from 'react';
import { Plus, Archive, ShieldCheck } from 'lucide-react';
import type { AcademyTrack } from '@platform/shared-types';

export default function CertificationList({
  tracks, selectedId, onSelect, onCreate, onArchive, busy,
}: {
  tracks: AcademyTrack[];
  selectedId: string | null;
  onSelect: (trackId: string) => void;
  onCreate: (title: string) => void;
  onArchive: (trackId: string) => void;
  busy: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  /** In-page confirm — never window.confirm. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const active = tracks.filter((t) => t.archived !== true);

  return (
    <section className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Certifications</h2>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 transition"
        >
          <Plus className="w-3.5 h-3.5" />
          New
        </button>
      </div>

      <div className="p-3 space-y-2">
        {adding && (
          <div className="border border-gray-200 rounded-xl bg-white p-3 space-y-2">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && title.trim()) { onCreate(title.trim()); setTitle(''); setAdding(false); }
                if (e.key === 'Escape') { setTitle(''); setAdding(false); }
              }}
              placeholder="Name this certification — e.g. Sales"
              className="w-full min-h-[44px] bg-white border border-gray-300 rounded-lg px-3 text-sm text-gray-900 outline-none focus:border-gray-900"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!title.trim()}
                onClick={() => { onCreate(title.trim()); setTitle(''); setAdding(false); }}
                className="min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white disabled:bg-gray-200 disabled:text-gray-400"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => { setTitle(''); setAdding(false); }}
                className="min-h-[44px] px-3 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {active.length === 0 && !adding && (
          <p className="text-sm text-gray-500 p-3">
            No certifications yet. Create one, then choose which modules it covers.
          </p>
        )}

        {active.map((t) => (
          <div key={t.trackId} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => onSelect(t.trackId)}
              className={`w-full text-left px-3 min-h-[44px] py-2 transition ${
                selectedId === t.trackId ? 'bg-gray-900 text-white' : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-semibold flex items-center gap-2">
                {t.title}
                {t.activatesPartner === true && (
                  <ShieldCheck className={`w-3.5 h-3.5 ${selectedId === t.trackId ? 'text-white' : 'text-gray-900'}`} />
                )}
              </span>
              <span className={`text-xs ${selectedId === t.trackId ? 'text-white/60' : 'text-gray-500'}`}>
                {t.moduleIds.length} module{t.moduleIds.length === 1 ? '' : 's'}
                {t.published ? ' · Published' : ' · Draft'}
                {t.activatesPartner === true ? ' · Activates partners' : ''}
              </span>
            </button>

            {confirming === t.trackId ? (
              <div className="border-t border-gray-200 p-3 bg-gray-50">
                <p className="text-xs text-gray-700">
                  Archive &ldquo;{t.title}&rdquo;? Reps stop seeing it, but every pass stays on record.
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => { onArchive(t.trackId); setConfirming(null); }}
                    className="min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white"
                  >
                    Archive
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(null)}
                    className="min-h-[44px] px-3 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700"
                  >
                    Keep
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming(t.trackId)}
                className="w-full border-t border-gray-200 px-3 min-h-[44px] text-left text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 inline-flex items-center gap-1.5 transition"
              >
                <Archive className="w-3.5 h-3.5" />
                Archive
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
